<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import {
  fetchChatHistory,
  fetchChatOnline,
  sendChatMessage,
  type ChatChannel,
  type ChatMessage,
} from '../game/chat-api';
import { BG } from '../game/art';
import { t } from '../i18n';

const channel = ref<ChatChannel>('global');
const messages = ref<ChatMessage[]>([]);
const draft = ref('');
const error = ref('');
const loading = ref(false);
const sending = ref(false);
const listEl = ref<HTMLElement | null>(null);
let poll: ReturnType<typeof setInterval> | null = null;
/** Everyone in the hall now (players + bots online). Null until the first answer. */
const online = ref<number | null>(null);
let onlinePoll: ReturnType<typeof setInterval> | null = null;
/** The figure drifts a few a minute (bot sessions, game/bots); half a minute is fresh enough. */
const ONLINE_POLL_MS = 30_000;

async function refreshOnline() {
  try { online.value = (await fetchChatOnline()).online; } catch { /* keep the last figure */ }
}

async function load(scroll = true) {
  loading.value = true;
  error.value = '';
  try {
    messages.value = await fetchChatHistory(channel.value);
    if (scroll) await scrollToBottom();
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
}

async function refresh() {
  try {
    const next = await fetchChatHistory(channel.value);
    const grew = next.length !== messages.value.length ||
      (next.at(-1)?.id !== messages.value.at(-1)?.id);
    messages.value = next;
    if (grew) await scrollToBottom();
  } catch { /* keep current on transient errors */ }
}

async function scrollToBottom() {
  await nextTick();
  if (listEl.value) listEl.value.scrollTop = listEl.value.scrollHeight;
}

async function send() {
  const text = draft.value.trim();
  if (!text || sending.value) return;
  sending.value = true;
  error.value = '';
  try {
    await sendChatMessage(channel.value, text);
    draft.value = '';
    await refresh();
  } catch (e) {
    error.value = (e as Error).message; // includes "Rate limited" from the core
  } finally {
    sending.value = false;
  }
}

/** Stable name colour, the way game chats do it: hash → hue, fixed S/L so every
 *  name clears contrast on the dark ground. Yours stays aurora. */
function nameHue(name: string): number {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return h % 360;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

onMounted(() => {
  load();
  poll = setInterval(refresh, 5000);
  void refreshOnline();
  onlinePoll = setInterval(refreshOnline, ONLINE_POLL_MS);
});
onBeforeUnmount(() => {
  if (poll) clearInterval(poll);
  if (onlinePoll) clearInterval(onlinePoll);
});
</script>

<template>
  <!-- ══ THE HALL ═════════════════════════════════════════════════════════════
       WAS a generic messenger: a bordered box, a `space-y-1.5` list of one-line
       rows (time · name · text run together inline), and an input with a button.
       That is the same widget every browser game ships and it said nothing about
       this one.

       NOW word travels the benches of a hall. Each message is a STAVE — a plank
       laid across the bench with the speaker's name struck into an iron tab at its
       head, the hour cut small at its foot, and the word itself set in book type.
       Your own staves are lit. The composer is a chisel slot in the hall's sill.
       Logic underneath is untouched: same poll, same channel, same send.
  ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- NOT A PAGE any more. Chat left the nav and became a drawer that opens over
       whatever screen you are on (see HeroScreen's .cw-* drawer), because word in a
       hall does not require you to walk to a room to hear it. So no page header,
       and the hall fills whatever it is mounted into. -->
  <section class="hall-host">
    <div class="hall">
      <!-- the eave: who is in earshot -->
      <p v-if="online !== null" class="hall-eave">
        <span class="hall-eave-dot" aria-hidden="true"></span>{{ t('chat.online', { count: online }) }}
      </p>

      <!-- the benches -->
      <div ref="listEl" class="hall-benches">
        <p v-if="loading && messages.length === 0" class="hall-quiet">{{ t('chat.listening') }}</p>
        <p v-else-if="messages.length === 0" class="hall-quiet">
          The hall is quiet. Speak, and it carries.
        </p>

        <p v-for="m in messages" :key="m.id" class="ln">
          <time class="ln-t" :datetime="m.createdAt">{{ fmtTime(m.createdAt) }}</time>
          <span class="ln-n" :style="{ color: m.isYou ? 'var(--color-aurora)' : `hsl(${nameHue(m.senderUsername ?? '?')}, 42%, 72%)` }">{{ m.senderUsername ?? t('chat.unknown') }}:</span>
          <span class="ln-w">{{ m.content }}</span>
        </p>
      </div>

      <!-- the sill -->
      <form class="hall-sill" @submit.prevent="send">
        <p v-if="error" class="hall-err">{{ error }}</p>
        <div class="hall-sill-row">
          <input
            v-model="draft"
            maxlength="500"
            :placeholder="t('chat.placeholder')"
            class="hall-slot"
          />
          <button
            type="submit"
            :disabled="sending || draft.trim().length === 0"
            class="btn-forge btn-forge--rune btn-forge--sm"
          >{{ sending ? '&hellip;' : t('chat.speak') }}</button>
        </div>
      </form>
    </div>
  </section>
</template>

<style scoped>
.hall-host { display: flex; flex-direction: column; min-height: 0; height: 100%; }
.hall {
  display: flex; flex-direction: column;
  flex: 1 1 auto; min-height: 0;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
}

/* ── the eave ─────────────────────────────────────────────────────────────── */
.hall-eave {
  display: flex; align-items: center; gap: 7px;
  margin: 8px 6px 0;
  font-family: var(--font-label, sans-serif);
  font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
  color: #9db0c6;
  font-variant-numeric: tabular-nums;
}
.hall-eave-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: var(--color-success);
  box-shadow: 0 0 6px color-mix(in srgb, var(--color-success) 70%, transparent);
}

/* ── the benches ──────────────────────────────────────────────────────────── */
.hall-benches {
  flex: 1 1 auto; min-height: 0;
  overflow-y: auto;
  margin: 8px 2px 0;
  padding: 8px;
  display: flex; flex-direction: column; gap: 1px;
  background: linear-gradient(180deg, rgba(9, 15, 24, 0.93), rgba(7, 12, 20, 0.96));
  box-shadow: var(--rim-ice-sunken);
  scrollbar-width: thin;
  scrollbar-color: color-mix(in srgb, var(--color-ice-dim) 35%, transparent) transparent;
}
.hall-quiet {
  font-family: var(--font-body, serif); font-style: italic;
  font-size: 13px; color: #bccbdb; padding: 10px 4px;
}

/* ── THE LINES — one line per message, the way every game chat reads:
   grey hour · coloured name · the word. Zero chrome per message. */
.ln {
  font-family: var(--font-body, serif);
  font-size: 13.5px; line-height: 1.45;
  color: #e9eff7;
  padding: 1px 2px;
  overflow-wrap: anywhere;
}
.ln-t {
  font-size: 10px; font-variant-numeric: tabular-nums;
  color: #7f93aa; margin-right: 6px;
}
.ln-n { font-weight: 700; margin-right: 5px; }

/* ── the sill ─────────────────────────────────────────────────────────────── */
.hall-sill {
  flex: none;
  padding: 9px 2px max(2px, env(safe-area-inset-bottom));
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.32));
  box-shadow: inset 0 3px 0 rgba(0, 0, 0, 0.45), inset 0 4px 6px rgba(0, 0, 0, 0.3);
}
.hall-sill-row { display: flex; gap: 9px; }
.hall-err { font-size: 11.5px; color: var(--color-error); margin-bottom: 6px; }
/* a slot chiselled into the sill, not a bordered text field */
.hall-scroll { position: relative; flex: 1 1 auto; display: flex; }
/* the rolled battens the parchment winds onto — a shade taller than the sheet,
   round-shouldered, lit from above like turned wood */
.hall-scroll::before, .hall-scroll::after {
  content: '';
  position: absolute; top: -4px; bottom: -4px; width: 13px; z-index: 1;
  border-radius: 6px;
  background: linear-gradient(90deg, #241708, #4a3116 38%, #6b4a22 52%, #3a2610 78%, #1d1206);
  box-shadow:
    inset 0 2px 2px rgba(255, 226, 170, 0.22),
    inset 0 -2px 3px rgba(0, 0, 0, 0.7),
    0 3px 6px rgba(0, 0, 0, 0.55);
  pointer-events: none;
}
.hall-scroll::before { left: -5px; }
.hall-scroll::after { right: -5px; }
.hall-slot {
  width: 100%;
  padding: 9px 18px;
  border: 0; border-radius: 2px;
  background:
    linear-gradient(90deg, rgba(74, 48, 18, 0.35), transparent 22px),
    linear-gradient(270deg, rgba(74, 48, 18, 0.35), transparent 22px),
    repeating-linear-gradient(0deg, rgba(93, 63, 28, 0.05) 0 1px, transparent 1px 3px),
    radial-gradient(120% 90% at 50% 0%, rgba(255, 246, 220, 0.5), transparent 60%),
    linear-gradient(180deg, #e6d5ac, #d7c08c 55%, #c8ae74);
  box-shadow:
    inset 0 1px 0 rgba(255, 248, 225, 0.65),
    inset 0 -2px 4px rgba(96, 64, 24, 0.4),
    0 3px 8px rgba(0, 0, 0, 0.5);
  color: #2a1c0c;
  caret-color: #5a3a14;
  font-family: var(--font-body, serif);
  font-size: 14px;
  transition: box-shadow 180ms ease;
}
.hall-slot::placeholder { color: #7a5f38; opacity: 0.8; font-style: italic; }
.hall-slot:focus {
  outline: none;
  box-shadow:
    inset 0 1px 0 rgba(255, 248, 225, 0.65),
    inset 0 -2px 4px rgba(96, 64, 24, 0.4),
    inset 0 0 18px rgba(201, 162, 76, 0.28),
    0 3px 10px rgba(0, 0, 0, 0.55);
}
</style>
